import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Select } from "./ui/Select";
import { Button, ButtonVariant, ButtonSize } from "@/shared/ui/Button";

interface PaginationProps {
  "data-testid": string;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalRecords: number;
  rowsPerPage: number;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  rowsPerPageOptions?: number[];
}

const Pagination: React.FC<PaginationProps> = ({
  "data-testid": dataTestId,
  currentPage,
  totalPages,
  onPageChange,
  totalRecords,
  rowsPerPage,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50],
}) => {
  const { t } = useTranslation();

  // Imperative live-region update via ref — React's vDOM diffing can sometimes
  // suppress the DOM mutation event that NVDA/JAWS rely on for aria-live announcement.
  // Writing textContent directly on the underlying DOM node guarantees the mutation fires.
  const liveRegionRef = useRef<HTMLSpanElement>(null);

  // Initialize to currentPage so the first useEffect run on mount is a no-op —
  // we only want to announce page TRANSITIONS, not the initial page state on load.
  const prevPageRef = useRef<number>(currentPage);

  useEffect(() => {
    if (prevPageRef.current === currentPage || !liveRegionRef.current) return;

    prevPageRef.current = currentPage;
    const msg = t("pagination.pageOf", { pageNumber: currentPage, totalPages });

    // Double-RAF clear-then-set pattern: canonical codebase approach (see RunScheduleTable,
    // SchedulesReferencesDropdown, RunStatusReferenceDropdown precedents).
    // First RAF clears the region; second RAF sets the new text — guarantees two separate
    // DOM mutations so NVDA/JAWS reliably fires the live-region announcement event.
    liveRegionRef.current.textContent = "";
    const raf1 = requestAnimationFrame(() => {
      if (!liveRegionRef.current) return;
      const raf2 = requestAnimationFrame(() => {
        if (liveRegionRef.current) {
          liveRegionRef.current.textContent = msg;
        }
      });
      // Cleanup inner RAF on unmount or re-run to prevent stale announcements
      return () => cancelAnimationFrame(raf2);
    });

    return () => cancelAnimationFrame(raf1);
  }, [currentPage, totalPages, t]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    onPageChange(page);
  };

  const startRecord = (currentPage - 1) * rowsPerPage + 1;
  const endRecord = Math.min(currentPage * rowsPerPage, totalRecords);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    const half = Math.floor(maxPagesToShow / 2);

    if (totalPages <= maxPagesToShow + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else if (currentPage <= half + 1) {
      for (let i = 1; i <= maxPagesToShow; i++) {
        pageNumbers.push(i);
      }

      pageNumbers.push("...");
      pageNumbers.push(totalPages);
    } else if (currentPage >= totalPages - half) {
      pageNumbers.push(1);
      pageNumbers.push("...");
      for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);
      pageNumbers.push("...");
      for (let i = currentPage - half; i <= currentPage + half; i++) {
        pageNumbers.push(i);
      }

      pageNumbers.push("...");
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const pages = getPageNumbers();

  return (
    <div
          className="flex justify-between items-center py-3 px-2 pr-24"
          data-testid={dataTestId}
        >
      <div className="flex items-center gap-3" data-testid={`${dataTestId}-controls`}>
        <nav
          className="flex items-center gap-1"
          aria-label={t("pagination.label")}
          data-testid={`${dataTestId}-nav`}
        >
          {/* First page button */}
          <Button
            variant={ButtonVariant.Outline}
            size={ButtonSize.Xs}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            data-testid={`${dataTestId}-first-button`}
            className="w-8 h-8 p-0"
            aria-label={t("pagination.firstPage")}
          >
            <ChevronsLeft className="w-4 h-4" aria-hidden="true" />
          </Button>
          {/* Previous page button */}
          <Button
            variant={ButtonVariant.Outline}
            size={ButtonSize.Xs}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            data-testid={`${dataTestId}-previous-button`}
            className="w-8 h-8 p-0"
            aria-label={t("pagination.previousPage")}
          >
            <ChevronLeft className="w-4 h-4" aria-hidden="true" />
          </Button>
          {/* Page numbers */}
          {pages.map((page, index) =>
            typeof page === "string" ? (
              <span
                key={`ellipsis-${index}`}
                className="px-3 py-2 text-sm font-medium text-text"
                data-testid={`${dataTestId}-ellipsis-${index}`}
                aria-hidden="true"
              >
                ...
              </span>
            ) : (
              <Button
                key={page}
                variant={currentPage === page ? ButtonVariant.Primary : ButtonVariant.Outline}
                size={ButtonSize.Xs}
                onClick={() => handlePageChange(page)}
                data-testid={`${dataTestId}-page-${page}-button`}
                className={`${page >= 1000 ? 'min-w-8 px-2' : 'w-8'} h-8 p-0`}
                aria-label={t("pagination.page", { pageNumber: page })}
                aria-current={currentPage === page ? "page" : undefined}
              >
                {page}
              </Button>
            ),
          )}
          {/* Next page button */}
          <Button
            variant={ButtonVariant.Outline}
            size={ButtonSize.Xs}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages === 0}
            data-testid={`${dataTestId}-next-button`}
            className="w-8 h-8 p-0"
            aria-label={t("pagination.nextPage")}
          >
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
          </Button>
          {/* Last page button */}
          <Button
            variant={ButtonVariant.Outline}
            size={ButtonSize.Xs}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
            data-testid={`${dataTestId}-last-button`}
            className="w-8 h-8 p-0"
            aria-label={t("pagination.lastPage")}
          >
            <ChevronsRight className="w-4 h-4" aria-hidden="true" />
          </Button>
        </nav>
        {/* Imperative textContent update via ref — bypasses React vDOM diffing so
            NVDA/JAWS always fires the live-region mutation event (see useEffect above).
            role="status" implies aria-live="polite" and aria-atomic="true" per ARIA spec;
            the explicit attributes are retained as a defensive override because some
            older NVDA/JAWS versions did not honour the implicit values correctly. */}
        <span
          ref={liveRegionRef}
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        />
      </div>

      <div className="flex items-center gap-3">
        <p className="text-sm text-text" data-testid={`${dataTestId}-info`}>
          {t("pagination.showing", {
            start: startRecord.toLocaleString("en-US"),
            end: endRecord.toLocaleString("en-US"),
            total: totalRecords.toLocaleString("en-US"),
          })}
        </p>
        <div className="flex items-center gap-2">
          <Select
            value={rowsPerPage}
            onChange={onRowsPerPageChange}
            useNative={true}
            className="min-w-[80px]"
            label=""
            aria-label={t("pagination.rowsPerPage")}
            data-testid={`${dataTestId}-rows-per-page-select`}
            direction="up"
            options={rowsPerPageOptions.map((option) => ({
              value: option,
              label: String(option),
            }))}
          />
          <span className="text-sm text-text">{t("pagination.resultsPerPage")}</span>
        </div>
      </div>
    </div>
  );
};

export default React.memo(Pagination);
